import { CategoryNavItem } from 'landing-pages/data-dictionary/sections/CategorySidebar/CategorySidebar.types';
import { DataField } from 'landing-pages/data-dictionary/sections/CategoryTable/CategoryTable.types';

/**
 * Translate function from useInt(). Only editorial copy (category names, the
 * total-fields label) is wrapped with t(). The field-catalogue arrays below
 * (field names, descriptions, types, examples) are product-schema reference
 * data and stay in English as plain constants.
 */
type Translate = (key: string) => string;

export const getCategories = (t: Translate): CategoryNavItem[] => [
  { id: 'p-registry', label: t('Company Registry'), count: 18 },
  { id: 'p-officers', label: t('Officers & Directors'), count: 12 },
  { id: 'p-financial', label: t('Financial Data'), count: 49 },
  { id: 'p-group', label: t('Group Structure'), count: 24 },
  { id: 'p-share', label: t('Shareholders'), count: 13 },
  { id: 'p-credit', label: t('Credit Risk'), count: 10 },
  { id: 'p-firmo', label: t('Firmographic Data'), count: 23 },
  { id: 'p-investment', label: t('M&A Activity'), count: 32 },
  { id: 'p-exec', label: t('Key Executives'), count: 11 },
  { id: 'p-digital', label: t('Digital Insights'), count: 10 },
];

export const getTotalFieldsLabel = (t: Translate) => t('10 categories · 202 documented fields');

export const REGISTRY_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

export const OFFICERS_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </svg>
);

export const FINANCIAL_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20" />
    <path d="M17 6.5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

export const GROUP_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="2" width="6" height="6" rx="1" />
    <rect x="2" y="16" width="6" height="6" rx="1" />
    <rect x="16" y="16" width="6" height="6" rx="1" />
    <path d="M12 8v4" />
    <path d="M5 16v-2h14v2" />
  </svg>
);

export const SHARE_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3v9l6.5 6.5" />
  </svg>
);

export const CREDIT_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M12 8v4" />
    <path d="M12 16h.01" />
  </svg>
);

export const FIRMO_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 21h18" />
    <path d="M5 21V7l7-4 7 4v14" />
    <path d="M10 21v-5h4v5" />
    <path d="M9 10h.01" />
    <path d="M15 10h.01" />
  </svg>
);

export const INVESTMENT_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 17l6-6 4 4 7-7" />
    <path d="M14 8h6v6" />
  </svg>
);

export const EXEC_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

export const DIGITAL_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

export const REGISTRY_FIELDS: DataField[] = [
  {
    name: 'Global Database ID',
    description: 'Unique identifier for each company',
    type: 'alnum',
    typeLabel: 'Alphanumeric',
    example: 'GDB12345678',
  },
  {
    name: 'Company Legal Name',
    description: 'Official registered name of the business',
    type: 'text',
    typeLabel: 'Text',
    example: 'Acme Corporation Ltd',
  },
  {
    name: 'Trading Name',
    description: 'Name under which the company operates if different from legal name',
    type: 'text',
    typeLabel: 'Text',
    example: 'Acme Tech',
  },
  {
    name: 'Registration Number',
    description: 'Official company registration number',
    type: 'alnum',
    typeLabel: 'Alphanumeric',
    example: '8765432',
  },
  {
    name: 'VAT Number',
    description: 'Company VAT/tax identification number',
    type: 'alnum',
    typeLabel: 'Alphanumeric',
    example: 'GB123456789',
  },
  {
    name: 'Year of Founding',
    description: 'Date when company was officially registered',
    type: 'date',
    typeLabel: 'Date',
    example: '15/05/2010',
  },
  {
    name: 'Company Status',
    description: 'Current operational status',
    type: 'text',
    typeLabel: 'Text',
    example: 'Active, Inactive, Intermediate',
  },
  {
    name: 'Legal Form',
    description: 'Legal structure/type of the company',
    type: 'text',
    typeLabel: 'Text',
    example: 'Private Limited, Public Limited, LLP',
  },
  {
    name: 'Country',
    description: 'Country where company is registered',
    type: 'text',
    typeLabel: 'Text',
    example: 'United Kingdom',
  },
  {
    name: 'Region',
    description: 'Region/state within the country',
    type: 'text',
    typeLabel: 'Text',
    example: 'London, South East, Midwest',
  },
  {
    name: 'Address Line 1',
    description: 'Primary street address',
    type: 'text',
    typeLabel: 'Text',
    example: '7 Queensbridge',
  },
  {
    name: 'Address Line 2',
    description: 'Additional address information',
    type: 'text',
    typeLabel: 'Text',
    example: "Artisans' House",
  },
  {
    name: 'City or State',
    description: 'City where company is located',
    type: 'text',
    typeLabel: 'Text',
    example: 'London',
  },
  { name: 'ZIP', description: 'Postal/ZIP code', type: 'alnum', typeLabel: 'Alphanumeric', example: 'EC1A 1AA' },
  {
    name: 'GEO Code Latitude',
    description: 'Geographic coordinates — latitude',
    type: 'dec',
    typeLabel: 'Decimal',
    example: '51.5074',
  },
  {
    name: 'GEO Code Longitude',
    description: 'Geographic coordinates — longitude',
    type: 'dec',
    typeLabel: 'Decimal',
    example: '-0.1278',
  },
  {
    name: 'Office Locations',
    description: 'List of additional office locations',
    type: 'arr',
    typeLabel: 'Text Array',
    example: '[London HQ, Manchester, Leeds]',
  },
  {
    name: 'Industry Classification Codes',
    description: 'Official industry classification codes (varies by country)',
    type: 'obj',
    typeLabel: 'Object',
    example: 'See Industry Classification section',
  },
];

export const OFFICERS_FIELDS: DataField[] = [
  {
    name: 'Officer ID',
    description: 'Unique identifier for the officer/director record',
    type: 'alnum',
    typeLabel: 'Alphanumeric',
    example: 'OFF-00294817',
  },
  {
    name: 'Full Name',
    description: 'Full legal name of the officer or director',
    type: 'text',
    typeLabel: 'Text',
    example: 'James Edward Thompson',
  },
  {
    name: 'Role',
    description: 'Official role held within the company',
    type: 'text',
    typeLabel: 'Text',
    example: 'Director, Secretary, CEO, CFO',
  },
  {
    name: 'Date of Birth',
    description: 'Month and year of birth (where publicly filed)',
    type: 'date',
    typeLabel: 'Date',
    example: 'March 1975',
  },
  {
    name: 'Nationality',
    description: 'Country of citizenship as declared in filings',
    type: 'text',
    typeLabel: 'Text',
    example: 'British',
  },
  {
    name: 'Country of Residence',
    description: 'Country where the officer currently resides',
    type: 'text',
    typeLabel: 'Text',
    example: 'United Kingdom',
  },
  {
    name: 'Appointed On',
    description: 'Date the officer was appointed to the role',
    type: 'date',
    typeLabel: 'Date',
    example: '15/03/2018',
  },
  {
    name: 'Resigned On',
    description: 'Date the officer resigned from the role (if applicable)',
    type: 'date',
    typeLabel: 'Date',
    example: 'null / 01/06/2024',
  },
  {
    name: 'Status',
    description: 'Whether the appointment is currently active or resigned',
    type: 'text',
    typeLabel: 'Text',
    example: 'Active, Resigned',
  },
  {
    name: 'Occupation',
    description: 'Declared occupation of the officer',
    type: 'text',
    typeLabel: 'Text',
    example: 'Company Director, Solicitor, Accountant',
  },
  {
    name: 'Other Directorships',
    description: 'Number of other companies where this individual holds or held directorships',
    type: 'int',
    typeLabel: 'Integer',
    example: '4',
  },
  {
    name: 'PEP/Sanctions Flag',
    description: 'Whether the officer has been flagged against PEP or sanctions lists',
    type: 'bool',
    typeLabel: 'Boolean',
    example: 'True / False',
  },
];

export const FINANCIAL_FIELDS: DataField[] = [
  {
    name: 'Latest Accounts Date Filed',
    description: 'Most recent date when financial accounts were submitted',
    type: 'date',
    typeLabel: 'Date',
    example: '31/12/2023',
  },
  { name: 'Ticker', description: 'Stock market ticker symbol', type: 'text', typeLabel: 'Text', example: 'ACME' },
  {
    name: 'Turnover',
    description: 'Annual sales/revenue',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£10,500,000',
  },
  {
    name: 'Reporting Period',
    description: 'Financial period covered',
    type: 'date',
    typeLabel: 'Date Range',
    example: '01/01/2023 – 31/12/2023',
  },
  {
    name: 'Working Capital',
    description: 'Current assets minus current liabilities',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£4,200,000',
  },
  {
    name: 'Total Assets',
    description: 'Value of all assets owned',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£15,700,000',
  },
  {
    name: 'Intangible Assets',
    description: 'Value of non-physical assets',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£3,200,000',
  },
  {
    name: 'Tangible Assets',
    description: 'Value of physical assets',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£8,500,000',
  },
  {
    name: 'Fixed Assets',
    description: 'Long-term assets',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£11,700,000',
  },
  { name: 'Stock', description: 'Inventory value', type: 'curr', typeLabel: 'Currency', example: '£1,200,000' },
  {
    name: 'Trade Debtors',
    description: 'Amount owed by customers',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£2,300,000',
  },
  { name: 'Cash', description: 'Available liquid funds', type: 'curr', typeLabel: 'Currency', example: '£3,500,000' },
  {
    name: 'Current Assets',
    description: 'Short-term assets',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£7,000,000',
  },
  {
    name: 'Trade Creditors',
    description: 'Amount owed to suppliers',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£1,800,000',
  },
  {
    name: 'Current Liabilities',
    description: 'Short-term liabilities',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£2,800,000',
  },
  {
    name: 'Total Liabilities',
    description: 'All financial obligations',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£4,800,000',
  },
  {
    name: 'Net Assets',
    description: 'Total assets minus total liabilities',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£10,900,000',
  },
  {
    name: 'Cost of Sales',
    description: 'Direct costs of goods sold',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£6,300,000',
  },
  {
    name: 'Gross Profit',
    description: 'Revenue minus cost of sales',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£4,200,000',
  },
  {
    name: 'Operating Profit',
    description: 'Profit from core business operations',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£2,800,000',
  },
  {
    name: 'EBITDA',
    description: 'Earnings before interest, taxes, depreciation, amortisation',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£3,200,000',
  },
  {
    name: 'Pre-Tax Profit',
    description: 'Profit before taxation',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£2,600,000',
  },
  { name: 'Taxation', description: 'Tax expenses', type: 'curr', typeLabel: 'Currency', example: '£650,000' },
  {
    name: 'Profit After Tax',
    description: 'Net profit after tax deductions',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£1,950,000',
  },
  {
    name: 'Retained Profits',
    description: 'Earnings kept in the business',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£1,750,000',
  },
  {
    name: 'Sundry Reserves',
    description: 'Miscellaneous reserve funds',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£850,000',
  },
  {
    name: 'Shareholder Funds',
    description: 'Total equity in the company',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£9,800,000',
  },
  {
    name: 'Capital Employed',
    description: 'Total assets minus current liabilities',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£12,900,000',
  },
  {
    name: 'P&L Account Reserve',
    description: 'Profit and loss reserves',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£3,200,000',
  },
  {
    name: 'Revaluation Reserve',
    description: 'Gains from asset revaluation',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£750,000',
  },
  {
    name: 'Consolidated A/cs',
    description: 'Whether accounts are consolidated',
    type: 'bool',
    typeLabel: 'Boolean',
    example: 'True / False',
  },
  {
    name: 'Gross Margin',
    description: 'Gross profit as percentage of revenue',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '40.00%',
  },
  {
    name: 'Operating Profit Margin',
    description: 'Operating profit as percentage of revenue',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '26.70%',
  },
  {
    name: 'EBITDA Margin',
    description: 'EBITDA as percentage of revenue',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '30.50%',
  },
  {
    name: 'Pre-Tax Profit Margin',
    description: 'Pre-tax profit as percentage of revenue',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '24.80%',
  },
  {
    name: 'Net Margin',
    description: 'Net profit as percentage of revenue',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '18.60%',
  },
  {
    name: 'Return on Assets',
    description: 'Net income as percentage of total assets',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '12.40%',
  },
  {
    name: 'Return on Capital Employed',
    description: 'EBIT as percentage of capital employed',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '21.70%',
  },
  {
    name: 'Return on Equity',
    description: 'Net income as percentage of shareholder equity',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '19.90%',
  },
  {
    name: 'Current Ratio',
    description: 'Current assets divided by current liabilities',
    type: 'dec',
    typeLabel: 'Decimal',
    example: '2.50',
  },
  {
    name: 'Cash to Current Liabilities',
    description: 'Cash divided by current liabilities',
    type: 'dec',
    typeLabel: 'Decimal',
    example: '1.25',
  },
  {
    name: 'Cash to Total Assets',
    description: 'Cash as percentage of total assets',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '22.30%',
  },
  {
    name: 'Liquidity Ratio',
    description: 'Quick assets divided by current liabilities',
    type: 'dec',
    typeLabel: 'Decimal',
    example: '2.10',
  },
  {
    name: 'Gearing Liability',
    description: 'Debt as percentage of equity',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '49.00%',
  },
  {
    name: 'Gearing Gross Debt',
    description: 'Gross debt as percentage of equity',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '37.80%',
  },
  {
    name: 'Gearing Net Debt',
    description: 'Net debt as percentage of equity',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '13.30%',
  },
  {
    name: 'Debt to Capital',
    description: 'Debt as percentage of total capital',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '27.10%',
  },
  {
    name: 'Inventory Turnover Ratio',
    description: 'Cost of goods sold divided by average inventory',
    type: 'dec',
    typeLabel: 'Decimal',
    example: '5.25',
  },
  {
    name: 'Cash to Turnover Ratio',
    description: 'Cash as percentage of revenue',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '33.30%',
  },
];

export const GROUP_FIELDS: DataField[] = [
  {
    name: 'DomesticUltimateUniqueID',
    description: 'Global Database unique ID of the Domestic Ultimate (highest entity in the same country)',
    type: 'alnum',
    typeLabel: 'Alphanumeric (ID)',
    example: 'GDB000987654',
  },
  {
    name: 'DomesticUltimateBusinessName',
    description: 'Legal name of the Domestic Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'ACME HOLDINGS UK LTD',
  },
  {
    name: 'DomesticUltimateStreetAddress',
    description: 'Street address of the Domestic Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: '10 Finsbury Sq',
  },
  {
    name: 'DomesticUltimateCityName',
    description: 'City / locality of the Domestic Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'London',
  },
  {
    name: 'DomesticUltimatePostalCode',
    description: 'Postal / ZIP code of the Domestic Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'EC2A 1AF',
  },
  {
    name: 'DomesticUltimateProvince',
    description: 'Region / state / province of the Domestic Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Greater London',
  },
  {
    name: 'DomesticUltimateCountry',
    description: 'Country name of the Domestic Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'United Kingdom',
  },
  {
    name: 'DomesticUltimateCountryCode',
    description: 'Country code (ISO 3166-1 alpha-2)',
    type: 'text',
    typeLabel: 'Text (2)',
    example: 'GB',
  },
  {
    name: 'GlobalUltimateUniqueID',
    description: 'Global Database unique ID of the Global Ultimate (highest entity worldwide)',
    type: 'alnum',
    typeLabel: 'Alphanumeric (ID)',
    example: 'GDB000123999',
  },
  {
    name: 'GlobalUltimateBusinessName',
    description: 'Legal name of the Global Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'ACME GROUP PLC',
  },
  {
    name: 'GlobalUltimateStreetAddress',
    description: 'Street address of the Global Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: '1 Market St',
  },
  {
    name: 'GlobalUltimateCityName',
    description: 'City / locality of the Global Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Dublin',
  },
  {
    name: 'GlobalUltimatePostalCode',
    description: 'Postal / ZIP code of the Global Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'D02 X285',
  },
  {
    name: 'GlobalUltimateProvince',
    description: 'Region / state / province of the Global Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Leinster',
  },
  {
    name: 'GlobalUltimateCountry',
    description: 'Country name of the Global Ultimate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Ireland',
  },
  {
    name: 'GlobalUltimateCountryCode',
    description: 'Country code (ISO 3166-1 alpha-2)',
    type: 'text',
    typeLabel: 'Text (2)',
    example: 'IE',
  },
  {
    name: 'ParentUniqueID',
    description: 'Global Database unique ID of the Immediate Parent / Headquarters entity',
    type: 'alnum',
    typeLabel: 'Alphanumeric (ID)',
    example: 'GDB000222333',
  },
  {
    name: 'ParentBusinessName',
    description: 'Legal name of the immediate parent entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'ACME EUROPE HOLDCO LTD',
  },
  {
    name: 'ParentStreetAddress',
    description: 'Street address of the immediate parent entity',
    type: 'text',
    typeLabel: 'Text',
    example: '20 Bishopsgate',
  },
  {
    name: 'ParentCityName',
    description: 'City / locality of the immediate parent entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'London',
  },
  {
    name: 'ParentPostalCode',
    description: 'Postal / ZIP code of the immediate parent entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'EC2N 4AJ',
  },
  {
    name: 'ParentProvince',
    description: 'Region / state / province of the immediate parent entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Greater London',
  },
  {
    name: 'ParentCountry',
    description: 'Country name of the immediate parent entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'United Kingdom',
  },
  {
    name: 'ParentCountryCode',
    description: 'Country code (ISO 3166-1 alpha-2)',
    type: 'text',
    typeLabel: 'Text (2)',
    example: 'GB',
  },
];

export const SHARE_FIELDS: DataField[] = [
  {
    name: 'Shareholder ID',
    description: 'Unique identifier for the shareholder record',
    type: 'alnum',
    typeLabel: 'Alphanumeric',
    example: 'SH-00183629',
  },
  {
    name: 'Shareholder Name',
    description: 'Full name of the individual or corporate shareholder',
    type: 'text',
    typeLabel: 'Text',
    example: 'Acme Holdings Ltd / John Smith',
  },
  {
    name: 'Shareholder Type',
    description: 'Whether the shareholder is an individual or a corporate entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Individual, Corporate',
  },
  {
    name: 'Country of Registration/Residence',
    description: 'Country where the shareholder is registered (corporate) or resides (individual)',
    type: 'text',
    typeLabel: 'Text',
    example: 'United Kingdom',
  },
  {
    name: 'Ownership Percentage',
    description: 'Percentage of shares held in the company',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '25.50%',
  },
  {
    name: 'Number of Shares',
    description: 'Total shares held by the shareholder',
    type: 'int',
    typeLabel: 'Integer',
    example: '500,000',
  },
  {
    name: 'Share Class',
    description: 'Class or type of shares held',
    type: 'text',
    typeLabel: 'Text',
    example: 'Ordinary, Preference, A Shares',
  },
  {
    name: 'Nominal Value',
    description: 'Face value per share as stated in company filings',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£1.00',
  },
  {
    name: 'UBO Flag',
    description: 'Whether this shareholder is identified as an Ultimate Beneficial Owner',
    type: 'bool',
    typeLabel: 'Boolean',
    example: 'True / False',
  },
  {
    name: 'PSC Nature of Control',
    description: 'Nature of significant control as per PSC register (UK) or equivalent',
    type: 'text',
    typeLabel: 'Text',
    example: 'Ownership of shares – 75% or more',
  },
  {
    name: 'Date Notified',
    description: 'Date the shareholding was notified/registered',
    type: 'date',
    typeLabel: 'Date',
    example: '28/01/2015',
  },
  {
    name: 'Corporate Shareholder GDB ID',
    description: 'Global Database ID of the corporate shareholder (for linkage/graph traversal)',
    type: 'alnum',
    typeLabel: 'Alphanumeric (ID)',
    example: 'GDB000445566',
  },
  {
    name: 'PEP/Sanctions Flag',
    description: 'Whether the shareholder has been flagged against PEP or sanctions lists',
    type: 'bool',
    typeLabel: 'Boolean',
    example: 'True / False',
  },
];

export const CREDIT_FIELDS: DataField[] = [
  {
    name: 'Credit Score',
    description: 'Numerical assessment of creditworthiness',
    type: 'int',
    typeLabel: 'Integer (0–100)',
    example: '78',
  },
  {
    name: 'Credit Limit Recommendation',
    description: 'Suggested maximum credit extension',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£250,000',
  },
  {
    name: 'Number of Court Judgments',
    description: 'Legal judgments for debt non-payment',
    type: 'int',
    typeLabel: 'Integer',
    example: '2',
  },
  {
    name: 'Number of Court Judgments Satisfied',
    description: 'Court judgments that have been paid',
    type: 'int',
    typeLabel: 'Integer',
    example: '1',
  },
  {
    name: 'Number of Mortgages',
    description: 'Registered mortgages against the company',
    type: 'int',
    typeLabel: 'Integer',
    example: '1',
  },
  {
    name: 'Average Invoice Value',
    description: 'Typical value of invoices issued/received',
    type: 'curr',
    typeLabel: 'Currency',
    example: '£12,500',
  },
  {
    name: 'Invoices Available',
    description: 'Total number of invoices in the system',
    type: 'int',
    typeLabel: 'Integer',
    example: '156',
  },
  { name: 'Paid Invoices', description: 'Number of invoices paid', type: 'int', typeLabel: 'Integer', example: '143' },
  {
    name: 'Outstanding Invoices',
    description: 'Number of invoices not yet paid',
    type: 'int',
    typeLabel: 'Integer',
    example: '13',
  },
  {
    name: 'Customer Payment Experiences',
    description: 'Reported payment behaviour with customers',
    type: 'text',
    typeLabel: 'Text',
    example: 'Pays on time, Pays 30+ days late',
  },
];

export const FIRMO_FIELDS: DataField[] = [
  {
    name: 'Phone Number',
    description: 'Main company phone number',
    type: 'text',
    typeLabel: 'Text',
    example: '+44 20 1234 5678',
  },
  { name: 'Fax', description: 'Company fax number', type: 'text', typeLabel: 'Text', example: '+44 20 1234 5679' },
  { name: 'Domain', description: 'Company website URL', type: 'url', typeLabel: 'URL', example: 'www.acmecorp.com' },
  {
    name: 'Email',
    description: 'General company email address',
    type: 'url',
    typeLabel: 'Email',
    example: 'info@acmecorp.com',
  },
  {
    name: 'Estimated Employee Numbers',
    description: 'Approximate count of employees',
    type: 'int',
    typeLabel: 'Integer/Range',
    example: '100–249',
  },
  {
    name: 'Estimated Revenue',
    description: 'Approximate annual turnover',
    type: 'curr',
    typeLabel: 'Currency/Range',
    example: '$10M–$25M',
  },
  {
    name: 'Business Description',
    description: 'Summary of company activities',
    type: 'text',
    typeLabel: 'Text',
    example: 'Provider of cloud-based CRM solutions…',
  },
  {
    name: 'Industry Tag',
    description: 'Primary industry category',
    type: 'text',
    typeLabel: 'Text',
    example: 'Information Technology',
  },
  {
    name: 'LinkedIn Company Profile',
    description: 'URL to LinkedIn company page',
    type: 'url',
    typeLabel: 'URL',
    example: 'linkedin.com/company/acmecorp',
  },
  {
    name: 'Facebook Company Profile',
    description: 'URL to Facebook company page',
    type: 'url',
    typeLabel: 'URL',
    example: 'facebook.com/acmecorp',
  },
  {
    name: 'Instagram Social Media Profile',
    description: 'URL to Instagram profile',
    type: 'url',
    typeLabel: 'URL',
    example: 'instagram.com/acmecorp',
  },
  {
    name: 'Logo',
    description: 'Company logo image',
    type: 'url',
    typeLabel: 'Image/URL',
    example: 'globaldatabase.com/logos/acme.png',
  },
  {
    name: 'Product Name',
    description: 'Name of the product or service line',
    type: 'text',
    typeLabel: 'Text',
    example: 'Acme Analytics Cloud',
  },
  {
    name: 'Product Category',
    description: 'Category the offering sits in',
    type: 'text',
    typeLabel: 'Text',
    example: 'Business Intelligence Software',
  },
  {
    name: 'Product Description',
    description: 'What the product does and who it serves',
    type: 'text',
    typeLabel: 'Text',
    example: 'Self-service reporting for finance teams…',
  },
  {
    name: 'Primary Revenue Model',
    description: 'Dominant way the company monetises',
    type: 'text',
    typeLabel: 'Text',
    example: 'Subscription, Transaction Fees, Licensing',
  },
  {
    name: 'Pricing Structure',
    description: 'Basis on which the offering is priced',
    type: 'text',
    typeLabel: 'Text',
    example: 'Per Seat, Usage-Based, Tiered, Freemium',
  },
  {
    name: 'News Headline',
    description: 'Title of the news item',
    type: 'text',
    typeLabel: 'Text',
    example: 'Acme Corporation opens Frankfurt office',
  },
  {
    name: 'News Publication Date',
    description: 'Date the item was published',
    type: 'date',
    typeLabel: 'Date',
    example: '14/07/2026',
  },
  {
    name: 'News Publisher',
    description: 'Outlet or source that published the item',
    type: 'text',
    typeLabel: 'Text',
    example: 'Reuters',
  },
  {
    name: 'News Event Category',
    description: 'Type of corporate event reported',
    type: 'text',
    typeLabel: 'Text',
    example: 'Funding, Leadership Change, Expansion',
  },
  {
    name: 'Adverse Media Flag',
    description: 'Whether the item indicates reputational or regulatory risk',
    type: 'bool',
    typeLabel: 'Boolean',
    example: 'True / False',
  },
  {
    name: 'News Source URL',
    description: 'Link to the originating article or release',
    type: 'url',
    typeLabel: 'URL',
    example: 'https://…',
  },
];

export const INVESTMENT_FIELDS: DataField[] = [
  {
    name: 'Valuation',
    description: 'Latest reported company valuation',
    type: 'curr',
    typeLabel: 'Currency',
    example: '$75B',
  },
  {
    name: 'Total Funding',
    description: 'Cumulative capital raised across all disclosed rounds',
    type: 'curr',
    typeLabel: 'Currency',
    example: '$2.89B',
  },
  {
    name: 'Funding Round Date',
    description: 'Year or date the round was announced',
    type: 'date',
    typeLabel: 'Date',
    example: '2025',
  },
  {
    name: 'Funding Round Type',
    description: 'Stage or instrument of the round',
    type: 'text',
    typeLabel: 'Text',
    example: 'Series A–E+, Growth Equity, Debt, IPO',
  },
  {
    name: 'Funding Round Amount',
    description: 'Capital raised in the round',
    type: 'curr',
    typeLabel: 'Currency',
    example: '$2B',
  },
  {
    name: 'Funding Round Valuation',
    description: 'Company valuation attached to the round',
    type: 'curr',
    typeLabel: 'Currency',
    example: '$75B',
  },
  {
    name: 'Funding Round Lead Investor',
    description: 'Investor leading the round',
    type: 'text',
    typeLabel: 'Text',
    example: 'Coatue',
  },
  {
    name: 'Acquisition Date',
    description: 'Year or date of the transaction',
    type: 'date',
    typeLabel: 'Date',
    example: '2025',
  },
  {
    name: 'Acquisition Target',
    description: 'Legal name of the acquired entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Cetelem Argentina',
  },
  {
    name: 'Acquisition Target Country',
    description: 'Country of the acquired entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'Argentina',
  },
  {
    name: 'Acquisition Target Sector',
    description: 'Industry classification of the target',
    type: 'text',
    typeLabel: 'Text',
    example: 'Alternative Lending',
  },
  {
    name: 'Acquisition Deal Value',
    description: 'Consideration paid, where disclosed',
    type: 'curr',
    typeLabel: 'Currency',
    example: '$3.8M',
  },
  {
    name: 'Acquisition Type',
    description: 'Structure of the transaction',
    type: 'text',
    typeLabel: 'Text',
    example: 'Full Acquisition, Partial Acquisition, Merger',
  },
  {
    name: 'Acquisition Status',
    description: 'Current state of the transaction',
    type: 'text',
    typeLabel: 'Text',
    example: 'Announced, Completed',
  },
  {
    name: 'Investor Name',
    description: 'Name of the investing entity',
    type: 'text',
    typeLabel: 'Text',
    example: 'SoftBank Vision Fund 2',
  },
  {
    name: 'Investor Type',
    description: 'Classification of capital provider',
    type: 'text',
    typeLabel: 'Text',
    example: 'VC, Corporate, Institutional, Angel',
  },
  {
    name: 'Investor Country',
    description: 'Country of registration',
    type: 'text',
    typeLabel: 'Text',
    example: 'Japan',
  },
  {
    name: 'Fund Name',
    description: 'Name of the individual fund vehicle',
    type: 'text',
    typeLabel: 'Text',
    example: 'Morphosis Capital Fund II',
  },
  {
    name: 'Fund Vintage Year',
    description: 'Year the fund began investing',
    type: 'int',
    typeLabel: 'Integer',
    example: '2022',
  },
  {
    name: 'Fund Size',
    description: 'Committed capital of the fund',
    type: 'curr',
    typeLabel: 'Currency',
    example: '€80,000,000',
  },
  {
    name: 'Assets Under Management',
    description: 'Total AUM across all fund vehicles',
    type: 'curr',
    typeLabel: 'Currency',
    example: '€130,000,000',
  },
  {
    name: 'Fund Status',
    description: 'Lifecycle stage of the fund',
    type: 'text',
    typeLabel: 'Text',
    example: 'Investing, Fully Deployed, Closed',
  },
  {
    name: 'Investment Date',
    description: 'Date capital was deployed into a portfolio company',
    type: 'date',
    typeLabel: 'Date',
    example: '01/06/2023',
  },
  {
    name: 'Portfolio Company',
    description: 'Company invested in',
    type: 'text',
    typeLabel: 'Text',
    example: 'Portfolio Company X',
  },
  {
    name: 'Portfolio Company Sector',
    description: 'Industry of the portfolio company',
    type: 'text',
    typeLabel: 'Text',
    example: 'Maritime Technology',
  },
  {
    name: 'Investment Round Type',
    description: 'Stage at which the investment was made',
    type: 'text',
    typeLabel: 'Text',
    example: 'Series B',
  },
  {
    name: 'Investment Amount',
    description: 'Capital deployed, where disclosed',
    type: 'curr',
    typeLabel: 'Currency',
    example: '€50,000,000',
  },
  { name: 'Exit Date', description: 'Date the exit completed', type: 'date', typeLabel: 'Date', example: '19/09/2024' },
  {
    name: 'Entity Sold',
    description: 'Company or holding divested',
    type: 'text',
    typeLabel: 'Text',
    example: 'Clinicile Dr. Leahu',
  },
  {
    name: 'Exit Buyer',
    description: 'Acquiring party in the exit',
    type: 'text',
    typeLabel: 'Text',
    example: 'Regina Maria Group',
  },
  {
    name: 'Exit Type',
    description: 'Mechanism of exit',
    type: 'text',
    typeLabel: 'Text',
    example: 'Full Exit, Partial Exit, IPO, Trade Sale',
  },
  {
    name: 'Exit Transaction Value',
    description: 'Consideration received, where disclosed',
    type: 'curr',
    typeLabel: 'Currency',
    example: '€48,000,000',
  },
];

export const EXEC_FIELDS: DataField[] = [
  { name: 'First Name', description: "Employee's first name", type: 'text', typeLabel: 'Text', example: 'John' },
  {
    name: 'Second Name',
    description: "Employee's last/family name",
    type: 'text',
    typeLabel: 'Text',
    example: 'Smith',
  },
  {
    name: 'Job Title',
    description: 'Position held in the company',
    type: 'text',
    typeLabel: 'Text',
    example: 'Chief Technology Officer',
  },
  {
    name: 'Department',
    description: 'Organisational department',
    type: 'text',
    typeLabel: 'Text',
    example: 'IT, Finance, Marketing, Sales',
  },
  {
    name: 'Seniority Level',
    description: 'Level of authority/experience',
    type: 'text',
    typeLabel: 'Text',
    example: 'C-Level, Director, Manager',
  },
  {
    name: 'Work Email',
    description: 'Direct email address',
    type: 'url',
    typeLabel: 'Email',
    example: 'john.smith@acmecorp.com',
  },
  {
    name: 'Work Phone Number',
    description: 'Direct contact number',
    type: 'text',
    typeLabel: 'Text',
    example: '+44 20 1234 5699',
  },
  { name: 'Nationality', description: 'Country of citizenship', type: 'text', typeLabel: 'Text', example: 'British' },
  {
    name: 'Appointed On Date',
    description: 'Date of appointment to role',
    type: 'date',
    typeLabel: 'Date',
    example: '15/03/2021',
  },
  {
    name: 'Country of Residence',
    description: 'Where employee is based',
    type: 'text',
    typeLabel: 'Text',
    example: 'United Kingdom',
  },
  {
    name: 'LinkedIn Profile',
    description: "URL to employee's LinkedIn page",
    type: 'url',
    typeLabel: 'URL',
    example: 'linkedin.com/in/johnsmith',
  },
];

export const DIGITAL_FIELDS: DataField[] = [
  {
    name: 'Traffic Sources – Direct',
    description: 'Percentage of traffic from direct visitors',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '30%',
  },
  {
    name: 'Traffic Sources – Referral',
    description: 'Percentage of traffic from referring websites',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '15%',
  },
  {
    name: 'Traffic Sources – Email',
    description: 'Percentage of traffic from email campaigns',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '5%',
  },
  {
    name: 'Traffic Sources – Organic',
    description: 'Percentage of traffic from search engines',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '35%',
  },
  {
    name: 'Traffic Sources – Social',
    description: 'Percentage of traffic from social media',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '10%',
  },
  {
    name: 'Traffic Sources – Ads',
    description: 'Percentage of traffic from paid advertising',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '5%',
  },
  {
    name: 'Bounce Rate',
    description: 'Percentage of visitors who leave after viewing one page',
    type: 'pct',
    typeLabel: 'Percentage',
    example: '45%',
  },
  {
    name: 'Pages Per Visit',
    description: 'Average number of pages viewed per session',
    type: 'dec',
    typeLabel: 'Decimal',
    example: '3.2',
  },
  {
    name: 'Average Visit Duration',
    description: 'Average time spent on the site per visit',
    type: 'date',
    typeLabel: 'Time',
    example: '2m 45s',
  },
  {
    name: 'Top Traffic by Country',
    description: 'Highest traffic source country',
    type: 'text',
    typeLabel: 'Text, Percentage',
    example: 'US (45%)',
  },
];
